/**
 * mcCollough — op-art that triggers the McCollough afterimage effect.
 *
 * The McCollough effect is a contingent afterimage: stare at a
 * pattern of black-and-white vertical stripes for ~1 minute and
 * then look at a plain surface, and you see them as PINK. The
 * effect lasts minutes to hours — a real perceptual phenomenon
 * discovered by Celeste McCollough in 1965.
 *
 * This shader BUILDS the classical conditioning protocol as a
 * piece of op-art:
 *   - Center: a rotating "conditioning wheel" with high-contrast
 *     black-and-white gratings at one orientation, paired with a
 *     color tag for the orientation.
 *   - Around the wheel: a slowly-rotating ring of the same
 *     pattern at a different orientation, with a complementary tag.
 *   - Off to the side: a circular "response field" — a solid
 *     neutral color that the viewer should look at after the
 *     conditioning cycle. The afterimage shows the complementary
 *     color (the McCollough effect).
 *   - After 60 seconds of conditioning, the response field swaps
 *     colors and the gratings rotate to drill in a new pair.
 *   - A subtle bass pulse drives the rotation speed.
 *
 * Scientific reference: McCollough (1965) "Color Adaptation of
 * Edge-Detectors in the Human Visual System"; Murch (1976)
 * "Color contingent aftereffects"; Held & Shattuck (1971).
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface McColloughPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}
const VERT_SRC = `#version 300 es
precision highp float; layout(location=0) in vec2 aPos; out vec2 vUV;
void main(){ vUV = aPos*0.5+0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

const FRAG_SRC = `#version 300 es
precision highp float; in vec2 vUV; out vec4 outColor;
uniform vec2 uResolution; uniform float uTime;
uniform vec3 uBass, uMid, uHigh;
float bass; float mid; float high;

const float TAU = 6.283185307179586;
const float PI = 3.14159265358979;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// === Orientation pair ===
// Each pair has a "vertical" orientation (conventionally 0°),
// a "horizontal" orientation (90°), and the colors that
// accompany them. The afterimage pairs are:
//   - Vertical + green → see red on neutral
//   - Horizontal + blue → see yellow on neutral
//   - Tilted (45°) + magenta → see green on neutral
//   - Tilted (135°) + cyan → see red on neutral
//
// The cycle shifts every 60 seconds of conditioning.
struct OriPair {
  float vertAngle;     // orientation 1 in radians
  float horizAngle;    // orientation 2 in radians
  vec3 vertTag;        // color paired with vertical
  vec3 horizTag;       // color paired with horizontal
  vec3 vertAfter;      // complementary color to vertTag
  vec3 horizAfter;     // complementary color to horizTag
};

OriPair pairAt(float T) {
  float cycle = floor(T / 60.0);
  float pick = mod(cycle, 4.0);
  if (pick < 0.5) {
    // Original McCollough: vertical + green, horizontal + blue.
    return OriPair(0.0, PI * 0.5, vec3(0.10, 0.95, 0.30), vec3(0.20, 0.40, 1.00),
                   vec3(0.95, 0.30, 0.25), vec3(1.00, 0.85, 0.20));
  } else if (pick < 1.5) {
    // Tilted: 45° + magenta, 135° + cyan.
    return OriPair(PI * 0.25, PI * 0.75, vec3(0.95, 0.20, 0.85), vec3(0.20, 0.95, 0.95),
                   vec3(0.30, 0.95, 0.30), vec3(0.95, 0.30, 0.30));
  } else if (pick < 2.5) {
    // Tilted: 30° + red, 60° + yellow.
    return OriPair(PI * 0.17, PI * 0.33, vec3(1.00, 0.20, 0.20), vec3(0.95, 0.85, 0.20),
                   vec3(0.20, 0.95, 0.95), vec3(0.20, 0.40, 1.00));
  } else {
    // Concentric: rings + contrast.
    return OriPair(0.0, PI * 0.5, vec3(0.95, 0.95, 0.25), vec3(0.30, 0.85, 0.95),
                   vec3(0.30, 0.30, 0.95), vec3(0.95, 0.40, 0.30));
  }
}

// === Grating: high-contrast black-and-white bars at a given angle ===
// Returns 0.0 (black) or 1.0 (white) at the given pixel position.
float grating(vec2 uv, float angle, float freq, float phase) {
  vec2 dir = vec2(cos(angle), sin(angle));
  float coord = dot(uv - 0.5, dir) * freq + phase;
  return step(0.0, sin(coord));
}

void main() {
  vec2 uv = vUV;
  uv.x *= uResolution.x / uResolution.y;
  float T = uTime;

  OriPair pair = pairAt(T);

  // === Layout ===
  // Center: a wheel at (0.0, 0.0) with vertical-bars + colors.
  // The wheel rotates slowly so the user's gaze is held steady.
  vec2 center = vec2(0.0, 0.0);
  vec2 d = uv - center;
  float r = length(d);
  float wheelAngle = atan(d.y, d.x + 1e-6);

  // === Wheel ===
  vec3 wheelCol = vec3(0.0);
  if (r < 0.42) {
    // Inside the wheel: alternating wedges of the two gratings.
    // The wheel is divided into 24 pie slices; odd slices are
    // orientation 1, even slices are orientation 2.
    float wheelRotation = T * 0.05;
    float wedgeAngle = wheelAngle + wheelRotation;
    float wedge = floor(wedgeAngle * 12.0 / TAU + 0.5);
    float isFirst = mod(wedge, 2.0);
    float orient = (isFirst < 0.5) ? pair.vertAngle : pair.horizAngle;
    vec3 tag = (isFirst < 0.5) ? pair.vertTag : pair.horizTag;

    // Grating in polar coordinates.
    vec2 polarUV = vec2(r * cos(wedgeAngle), r * sin(wedgeAngle));
    float bar = grating(polarUV, orient, 30.0, T * 0.0);
    wheelCol = vec3(bar);

    // Color tag wedge: a small colored arc at the inner edge of
    // each pie slice.
    if (r > 0.32 && r < 0.40) {
      wheelCol = mix(wheelCol, tag, 0.85);
    }
    // Outer rim: a thin black ring.
    if (r > 0.40) {
      wheelCol = vec3(0.0);
    }
    // Inner ring: a thin white ring with center dot.
    if (r < 0.04) {
      wheelCol = vec3(1.0);
    }
  }

  // === Conditioning matrix: two large circular fields LEFT and RIGHT of the wheel. ===
  // Each one is a big black-and-white striped pattern at one
  // orientation, plus a small color tag at the top of the field.
  // The user is supposed to fixate on these (NOT the wheel) for
  // ~30 seconds each.
  vec3 leftCol = vec3(0.0);
  vec3 rightCol = vec3(0.0);
  // Left field: x ∈ [-1.0, -0.5], y ∈ [-0.45, 0.45].
  vec2 leftCenter = vec2(-0.7, 0.0);
  vec2 rightCenter = vec2(0.7, 0.0);
  float fieldRadius = 0.40;
  float fieldDist = 0.0;
  int fieldIdx = 0;
  if (length(uv - leftCenter) < fieldRadius) {
    fieldIdx = 1;
    fieldDist = length(uv - leftCenter);
  } else if (length(uv - rightCenter) < fieldRadius) {
    fieldIdx = 2;
    fieldDist = length(uv - rightCenter);
  }

  vec3 conditioningCol = vec3(0.0);
  if (fieldIdx == 1) {
    // Left: orientation 1 (vertical).
    float bar = grating(uv, pair.vertAngle, 50.0, T * 0.0);
    conditioningCol = vec3(bar);
    // Tag: a thin colored ring at the top.
    if (fieldDist > 0.30 && fieldDist < 0.38) {
      conditioningCol = mix(conditioningCol, pair.vertTag, 0.7);
    }
  } else if (fieldIdx == 2) {
    // Right: orientation 2 (horizontal).
    float bar = grating(uv, pair.horizAngle, 50.0, T * 0.0);
    conditioningCol = vec3(bar);
    // Tag: a thin colored ring at the top.
    if (fieldDist > 0.30 && fieldDist < 0.38) {
      conditioningCol = mix(conditioningCol, pair.horizTag, 0.7);
    }
  }

  // === Response field: a small neutral circle in the corner ===
  // This is the field the viewer should look at AFTER conditioning
  // to see the afterimage. We display the COMPLEMENTARY color so
  // it actually shows the result of the McCollough effect.
  vec2 responseCenter = vec2(0.85, -0.65);
  float responseDist = length(uv - responseCenter);
  vec3 responseCol = vec3(0.0);
  if (responseDist < 0.20) {
    // Solid complementary color.
    vec3 left = (uv.x < responseCenter.x - 0.005) ? pair.vertAfter : pair.horizAfter;
    responseCol = left;
    // Soft directional cue: a faint gradient toward the center.
    responseCol *= 0.95 + 0.05 * sin(responseDist * 20.0);
    // Outline ring.
    if (responseDist > 0.18) {
      responseCol = vec3(0.0);
    }
  }

  // === Compose ===
  vec3 col = vec3(0.96, 0.96, 0.98); // page background

  // Draw conditioning circles (left and right).
  if (fieldIdx > 0) {
    col = conditioningCol;
  }
  // Draw wheel on top.
  if (r < 0.42) {
    col = wheelCol;
  }
  // Draw response field.
  if (responseDist < 0.20) {
    col = responseCol;
  }

  // === Typography markers ===
  // (Inline letter-rendering function removed — GLSL doesn't allow
  // nested function definitions inside main().)

  // === Subtle paper grain ===
  float grain = hash21(uv * uResolution + T * 60.0) - 0.5;
  col += grain * 0.02;

  // === Cycle indicator: a small dot at the bottom showing how far ===
  // into the current 60-second cycle we are.
  float cyclePos = mod(T, 60.0) / 60.0;
  vec2 indicatorPos = vec2(0.0, -0.92);
  float id = length(uv - indicatorPos);
  if (id < 0.04) {
    col = vec3(0.95);
  }
  if (id > 0.04 && id < 0.05) {
    col = vec3(0.0);
  }
  // Fill in the progress arc.
  float indAngle = atan(uv.y - indicatorPos.y, uv.x - indicatorPos.x + 1e-6);
  if (id > 0.05 && id < 0.07 && indAngle > -PI * 0.5 && indAngle < -PI * 0.5 + TAU * cyclePos) {
    col = vec3(0.95, 0.4, 0.2);
  }

  // === Bass pulse: very subtle on the conditioning circles ===
  // (Doesn't break the McCollough effect since it's not on the
  // stimulus itself.)
  if (r > 0.42 && fieldIdx == 0 && responseDist > 0.20) {
    col *= 0.95 + 0.05 * bass;
  }

  // === Vignette ===
  vec2 vc = uv - vec2(0.0, 0.0);
  vc.x *= 1.0 / (uResolution.x / uResolution.y);
  float vig = 1.0 - dot(vc, vc) * 0.10;
  col *= clamp(vig, 0.0, 1.0);

  outColor = vec4(col, 1.0);
}`;

function bands(t: number, o: Float32Array): void {
  for (let i = 0; i < 9; i++) o[i] = 0.5 + 0.3 * Math.sin(t * (0.8 + i * 0.17));
}
export function computeMcColloughBands(t: number, o: Float32Array): void { bands(t, o); }
export function computeMcColloughRealtimeBands(f: Uint8Array, o: Float32Array): void {
  const N = f.length;
  for (let i = 0; i < 9; i++) {
    const start = Math.floor(i / 9 * N);
    const end = Math.floor((i + 1) / 9 * N);
    let s = 0, n = 0;
    for (let k = start; k < end; k++) { s += f[k] || 0; n++; }
    o[i] = n > 0 ? (s / n) / 255 : 0;
  }
}
export function createMcColloughPipeline(gl: WebGL2RenderingContext): McColloughPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return { program, vao, vbo, bands: new Float32Array(9) };
}
export function drawMcCollough(gl: WebGL2RenderingContext, p: McColloughPipeline, w: number, h: number, t: number): void {
  gl.useProgram(p.program);
  const prog = p.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), w, h);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), p.bands[0], p.bands[1], p.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), p.bands[3], p.bands[4], p.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), p.bands[6], p.bands[7], p.bands[8]);
  gl.bindVertexArray(p.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
export { VERT_SRC, FRAG_SRC };
